import { LOCALES, DEFAULT_LOCALE, type Locale } from '@ai-radar/shared';

/**
 * Site-wide configuration.
 *
 * Everything URL-shaped derives from PUBLIC_BASE_URL so no production
 * domain is baked into the code.
 */

interface SiteInfo {
  name: string;
  tagline: string;
  description: string;
}

/** Brand strings per locale. The name stays Latin in both. */
export const SITE: Record<Locale, SiteInfo> = {
  en: {
    name: 'On AI Radar',
    tagline: 'Everything important in AI. Nothing else.',
    description:
      'A daily filter for people who work with AI. The important model releases, ' +
      'tool launches, product updates and pricing changes — and nothing else.',
  },
  ar: {
    name: 'On AI Radar',
    tagline: 'كل ما يهم في الذكاء الاصطناعي. لا شيء غير ذلك.',
    description:
      'مرشّح يومي لمن يعمل في الذكاء الاصطناعي: إصدارات النماذج المهمة، ' +
      'الأدوات الجديدة، تحديثات المنتجات وتغييرات الأسعار — ولا شيء غير ذلك.',
  },
};

/** Absolute origin, no trailing slash. */
export function baseUrl(): string {
  const raw = process.env.PUBLIC_BASE_URL ?? 'http://localhost:3000';
  return raw.replace(/\/+$/, '');
}

/** Absolute URL for a site-relative path. Used for canonical and OG tags. */
export function absoluteUrl(path = '/'): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl()}${suffix}`;
}

/** Base URL of the NestJS API, server-side only. */
export function apiUrl(): string {
  const raw = process.env.API_BASE_URL ?? 'http://localhost:4000/api';
  return raw.replace(/\/+$/, '');
}

/**
 * hreflang map for a locale-agnostic path.
 *
 * Every page declares both languages plus x-default pointing at English,
 * so Google serves the right version and the two do not compete with each
 * other in search results.
 *
 * `path` is the part AFTER the locale prefix, e.g. '/models/claude-opus-5'.
 */
export function localeAlternates(path = '/'): Record<string, string> {
  const clean = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;

  const languages: Record<string, string> = {};
  for (const locale of LOCALES) {
    languages[locale] = absoluteUrl(`/${locale}${clean}`);
  }
  languages['x-default'] = absoluteUrl(`/${DEFAULT_LOCALE}${clean}`);

  return languages;
}
